import {atom, useSetAtom, WritableAtom} from "jotai";
import {atomWithStorage} from "jotai/utils";

// HYDRATE is a sentinel value to trigger a fetch
const HYDRATE = Symbol();

/**
 * Make a fetcher atom that fetches data from a function and stores it in a base atom.
 *
 * We use the pattern suggested by the creator of Jotai at https://github.com/pmndrs/jotai/issues/691#issuecomment-913905998
 * This works as follows:
 *  - baseDataAtom stores the data.
 *  - dataAtom is a derived atom that serves the data, and is also responsible for calling the fetchFn.
 *  - The set function of dataAtom is overridden. When called with the HYDRATE symbol, it triggers the fetch.
 *  - isLoadingAtom simply tracks whether the fetch is in progress.
 *  - useRehydrateData is a hook can be used to rehydrate the data atom.
 *
 * @param fetchFn - A function that returns a promise of the data to be fetched
 * @param key - A name for the `localStorage` var this gets saved to
 * @param initialValue - The initial value of the atom, defaults to null
 * @returns An object with the data atom and the isLoading atom
 */
const makeFetchStoreAtom = <T>(
	fetchFn: () => Promise<T>,
	key: string,
	initialValue: T | null = null
) => {
	const isLoadingAtom = atom<boolean>(true);
	const baseDataAtom = atomWithStorage<T | null>(key, initialValue);
	type DataAtomType = WritableAtom<T | null, [T | null | typeof HYDRATE], void>;
	const dataAtom: DataAtomType = atom(
		(get) => get(baseDataAtom),
		(_, set, value) => {
			if (value === HYDRATE) {
				set(isLoadingAtom, true);
				fetchFn().then((data) => {
					set(isLoadingAtom, false);
					set(baseDataAtom, data);
				});
			} else {
				// TODO Idk but I saw the below comment and it kinda gives "fix me pls" vibes
				// Generally we shouldn't be setting the data directly, but allow it for testing
				set(baseDataAtom, value);
			}
		}
	);
	dataAtom.onMount = (set) => set(HYDRATE);

	const useRehydrateData = () => {
		const setData = useSetAtom(dataAtom);
		return () => {
			setData(HYDRATE);
		};
	};

	return {dataAtom, isLoadingAtom, useRehydrateData};
};

export default makeFetchStoreAtom;
